import { FC } from "react";
import { capitalizeFirstLetter, useIsMobile } from "../../../../utils";
import { RecommendedSizeType } from "../../../../types/result";
import { resultScreenUI } from "../../../../configs";
import { findBrandByDomain } from "../../../../configs/configLoader";

import "./index.css";

interface IPropsSizeSelectorsVB {
  sizeLabelRank0: RecommendedSizeType | null | undefined;
  sizeLabelRank1: RecommendedSizeType | null | undefined;
  sizeLabelRank2: RecommendedSizeType | null | undefined;
  selectedVariant: string;
  setSelectedVariant: React.Dispatch<React.SetStateAction<string>>;
  fit_feedbacks: string[];
  selectedSize: RecommendedSizeType | null;
  isSizeUnavailable: boolean;
}

type SelectorSizeType = "unfocused" | "focused" | "unavailable";

const SizeSelectorsVB: FC<IPropsSizeSelectorsVB> = ({
  sizeLabelRank0,
  sizeLabelRank1,
  sizeLabelRank2,
  selectedVariant,
  setSelectedVariant,
  fit_feedbacks,
  selectedSize,
  isSizeUnavailable,
}) => {
  const isMobile = useIsMobile();

  const brandDefined = findBrandByDomain();

  const isGivenchy = brandDefined?.name === "Givenchy";
  const isGivenchyMobile = isGivenchy && isMobile;

  const getSelectorSizeStyles = (type: SelectorSizeType) => {
    const sizeStyles = resultScreenUI.sizeSelector[type]!;

    const selectorSizeStyles = {
      backgroundColor:
        sizeStyles.backgroundColor as React.CSSProperties["backgroundColor"],
      fontWeight: sizeStyles.fontWeight as React.CSSProperties["fontWeight"],
      fontSize: sizeStyles.fontSize as React.CSSProperties["fontSize"],
      textTransform:
        sizeStyles.textTransform as React.CSSProperties["textTransform"],
      color: sizeStyles.fontColor as React.CSSProperties["color"],
      border: `1px solid ${sizeStyles?.borderColor}`,
      letterSpacing: (sizeStyles as any)?.letterSpacing,
      fontStyle: "normal",
    };

    if (isSizeUnavailable && type === "focused") {
      selectorSizeStyles.color = resultScreenUI.sizeSelector["unfocused"]
        .fontColor as React.CSSProperties["color"];
      (selectorSizeStyles as any).textDecoration = "line-through";
      selectorSizeStyles.border = "1px solid transparent";
    }

    return selectorSizeStyles;
  };

  const firstStyles =
    selectedVariant === "0"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 && !isSizeUnavailable
            ? "unavailable"
            : "focused",
        )
      : getSelectorSizeStyles("unfocused");

  const secondStyles =
    selectedVariant === "1"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 ? "unavailable" : "focused",
        )
      : getSelectorSizeStyles("unfocused");

  const thirdStyles =
    selectedVariant === "2"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 && !isSizeUnavailable
            ? "unavailable"
            : "focused",
        )
      : getSelectorSizeStyles("unfocused");

  return (
    <div
      className="sizes-menu"
      style={{
        marginTop: isMobile ? "40px" : isGivenchy ? "20px" : "30px",
        marginBottom: isMobile ? "40px" : isGivenchy ? "20px" : "30px",
        gap: isGivenchy ? "20px" : isMobile ? "35px" : "34px",
      }}
    >
      {sizeLabelRank0 && (
        <div>
          <div
            tabIndex={0}
            aria-label={
              sizeLabelRank0
                ? `${sizeLabelRank0.label}. ${
                    firstStyles?.textTransform === "capitalize"
                      ? capitalizeFirstLetter(fit_feedbacks[0])
                      : fit_feedbacks[0]
                  }`
                : ""
            }
            className={`menu-item ${selectedVariant === "0" && "active"}`}
            onClick={() => {
              if (!!sizeLabelRank0) setSelectedVariant("0");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (!!sizeLabelRank0) setSelectedVariant("0");
              }
            }}
            style={{
              ...firstStyles,
              cursor: !sizeLabelRank0 ? "default" : "pointer",
              textTransform:
                firstStyles?.textTransform === "capitalize"
                  ? "none"
                  : firstStyles?.textTransform,
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              alignItems: "center",
              width: "auto",
              minWidth: "85px",
              height: "auto",
              minHeight: "85px",
              padding: "20px 10px",
            }}
          >
            <p
              style={{
                fontSize: "36px",
                margin: 0,
                position: "relative",
                top: isGivenchyMobile ? "1px" : 0,
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              {!sizeLabelRank0 ? "" : sizeLabelRank0?.label}
            </p>
          </div>
          <p
            style={{
              width: "100%",
              whiteSpace: "nowrap",
              fontSize: firstStyles?.fontSize || "10px",
              marginTop: isGivenchy ? "20px" : "10px",
              textDecoration:
                selectedVariant === "0" && isGivenchy ? "underline" : "none",
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {!sizeLabelRank0
              ? ""
              : firstStyles?.textTransform === "capitalize"
                ? capitalizeFirstLetter(fit_feedbacks[0])
                : sizeLabelRank0?.variant_id
                  ? fit_feedbacks[0]
                  : ""}
          </p>
        </div>
      )}

      {sizeLabelRank1 && (
        <div>
          <div
            tabIndex={0}
            aria-label={
              sizeLabelRank1
                ? `${sizeLabelRank1.label}. ${
                    secondStyles?.textTransform === "capitalize"
                      ? capitalizeFirstLetter(fit_feedbacks[1])
                      : fit_feedbacks[1]
                  }`
                : ""
            }
            className={`menu-item ${selectedVariant === "1" && "active"}`}
            onClick={() => setSelectedVariant("1")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setSelectedVariant("1");
              }
            }}
            style={{
              ...secondStyles,
              cursor: "pointer",
              textTransform:
                secondStyles?.textTransform === "capitalize"
                  ? "none"
                  : secondStyles?.textTransform,
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              alignItems: "center",
              width: "auto",
              minWidth: "85px",
              height: "auto",
              minHeight: "85px",
              padding: "20px 10px",
            }}
          >
            <p
              style={{
                fontSize: "36px",
                margin: 0,
                position: "relative",
                top: isGivenchyMobile ? "1px" : 0,
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              {!sizeLabelRank1 ? "" : sizeLabelRank1?.label}
            </p>
          </div>
          <p
            style={{
              width: "100%",
              whiteSpace: "nowrap",
              fontSize: secondStyles?.fontSize || "10px",
              marginTop: isGivenchy ? "20px" : "10px",
              textDecoration:
                selectedVariant === "1" && isGivenchy ? "underline" : "none",
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {secondStyles?.textTransform === "capitalize"
              ? capitalizeFirstLetter(fit_feedbacks[1])
              : fit_feedbacks[1]}
          </p>
        </div>
      )}

      {sizeLabelRank2 && (
        <div>
          <div
            tabIndex={0}
            aria-label={
              sizeLabelRank2
                ? `${sizeLabelRank2.label}. ${
                    thirdStyles?.textTransform === "capitalize"
                      ? capitalizeFirstLetter(fit_feedbacks[2])
                      : fit_feedbacks[2]
                  }`
                : ""
            }
            className={`menu-item ${selectedVariant === "2" && "active"}`}
            onClick={() => {
              if (!!sizeLabelRank2) setSelectedVariant("2");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (!!sizeLabelRank2) setSelectedVariant("2");
              }
            }}
            style={{
              ...thirdStyles,
              cursor: !sizeLabelRank2 ? "default" : "pointer",
              textTransform:
                thirdStyles?.textTransform === "capitalize"
                  ? "none"
                  : thirdStyles?.textTransform,
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              alignItems: "center",
              width: "auto",
              minWidth: "85px",
              height: "auto",
              minHeight: "85px",
              padding: "20px 10px",
            }}
          >
            <p
              style={{
                fontSize: "36px",
                margin: 0,
                position: "relative",
                top: isGivenchyMobile ? "1px" : 0,
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              {!sizeLabelRank2 ? "" : sizeLabelRank2?.label}
            </p>
          </div>
          <p
            style={{
              width: "100%",
              whiteSpace: "nowrap",
              fontSize: thirdStyles?.fontSize || "10px",
              marginTop: isGivenchy ? "20px" : "10px",
              textDecoration:
                selectedVariant === "2" && isGivenchy ? "underline" : "none",
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {!sizeLabelRank2
              ? ""
              : thirdStyles?.textTransform === "capitalize"
                ? capitalizeFirstLetter(fit_feedbacks[2])
                : sizeLabelRank2?.variant_id
                  ? fit_feedbacks[2]
                  : ""}
          </p>
        </div>
      )}
    </div>
  );
};

export default SizeSelectorsVB;
